import React, { FC } from 'react';
import sanityImgUtil from 'utils/sanityImgUtil';
import generateWriterLink from 'utils/generateWriterLink';

import {
  Avatar,
  Container,
  TextContainer,
  AuthorReviewerContainer,
  PracticeNameContainer,
  DateContainer,
  InlineLinkContainer,
  InnerContainer,
  Wrapper,
} from './style';
import { Img, Link, PracticeName, SocialShareButtons } from 'styled';
import { format } from 'date-fns';

import type { Quiz } from 'types';

import useLanguage from 'hooks/useLanguage';
import { useGlobalSettings } from 'state/selectors/globalSettings';

type Props = {
  quiz: Quiz;
};

const QuizMetaInfo: FC<Props> = ({ quiz }) => {
  const {
    meta: { author, reviewer, modifiedDate },
  } = quiz;

  const Language = useLanguage();
  const globalSettings = useGlobalSettings();

  return (
    <Wrapper>
      <Container>
        <AuthorReviewerContainer>
          <InnerContainer>
            {author && (
              <TextContainer>
                {author.image.src && (
                  <Avatar>
                    <Img
                      alt={author.image.alt}
                      className="fit-cover w100"
                      src={sanityImgUtil(author.image, 72)}
                    />
                  </Avatar>
                )}
                <span>
                  {Language.t('ArticleNav.writtenBy')}{' '}
                  <Link
                    variant="underline-black-xs"
                    ariaLabel={Language.t('Global.teamMemberButtonAriaLabel', {
                      name: author.fullName,
                    })}
                    label={`${author.fullName}${Language.t('Global.period')}`}
                    to={generateWriterLink(author.slug)}
                    wrap={true}
                    inline={true}
                  />
                  {!!author.practiceNames.length && (
                    <PracticeNameContainer>
                      <PracticeName
                        typography="primary"
                        buttonVariant="underline-black-xs"
                        practiceName={author.practiceNames[0]}
                      />
                    </PracticeNameContainer>
                  )}
                </span>
              </TextContainer>
            )}
            {reviewer && (
              <TextContainer>
                {reviewer.image.src && (
                  <Avatar>
                    <Img
                      alt={reviewer.image.alt}
                      className="fit-cover w100"
                      src={sanityImgUtil(reviewer.image, 72)}
                    />
                  </Avatar>
                )}
                <span className="inline-block">
                  {Language.t('ArticleNav.reviewedBy')}{' '}
                  <InlineLinkContainer>
                    <Link
                      variant="underline-black-xs"
                      ariaLabel={Language.t(
                        'Global.teamMemberButtonAriaLabel',
                        {
                          name: reviewer.fullName,
                        },
                      )}
                      containerClassName="inline"
                      className="inline"
                      label={`${reviewer.fullName}${Language.t('Global.period')}`}
                      to={generateWriterLink(reviewer.slug)}
                      wrap={true}
                      inline={true}
                    />
                  </InlineLinkContainer>
                  {!!reviewer.practiceNames.length && (
                    <PracticeNameContainer>
                      <PracticeName
                        typography="primary"
                        buttonVariant="underline-black-xs"
                        practiceName={reviewer.practiceNames[0]}
                      />
                    </PracticeNameContainer>
                  )}
                </span>
              </TextContainer>
            )}
          </InnerContainer>
        </AuthorReviewerContainer>
      </Container>
      <div className="mt-1 flex w-full items-center justify-between">
        <p className="flex flex-col text-xs sm:flex-row">
          {Language.t('ArticleNav.lastUpdated')}&nbsp;
          <span>{format(new Date(modifiedDate), 'MMMM d, yyyy')}</span>
        </p>
        <SocialShareButtons
          renderBlackIcons={true}
          media={globalSettings.seo.image.src}
        />
      </div>
    </Wrapper>
  );
};

export default QuizMetaInfo;
